import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { Button } from "@/components/design/Button";
import { Dialog } from "@/components/common/Dialog";
import { FeaturedCard } from "@/components/grow/FeaturedCard";
import { fetchCatalogue } from "@/lib/games/roleplay/api";
import { type Catalogue } from "@/lib/games/roleplay/contract";
import { earliestExpiry, gameEntry, gameHref } from "@/lib/games/roleplay/entry";
import { ROLEPLAY_GAME_KEY } from "@/lib/games/roleplay/genesis";
import { replacePlaneGame } from "@/lib/games/roleplay/new-game";

type CardState = { status: "loading" } | { status: "ready"; catalogue: Catalogue } | { status: "failed" };

export function RoleplayCard() {
  const intl = useIntl();
  const navigate = useNavigate();
  const client = useApolloClient();
  const [state, setState] = useState<CardState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchCatalogue(controller.signal).then(
      (catalogue) => setState({ status: "ready", catalogue }),
      () => { if (!controller.signal.aborted) setState({ status: "failed" }); },
    );
    return () => controller.abort();
  }, []);

  const entry = state.status === "ready" ? gameEntry(state.catalogue) : null;
  const expiry = state.status === "ready" ? earliestExpiry(state.catalogue) : null;
  const canStart = state.status === "ready" && state.catalogue.games.some((game) => game.gameKey === ROLEPLAY_GAME_KEY && game.available);
  const startNew = async () => {
    if (!entry || entry.kind === "unavailable") return;
    setBusy(true);
    setError(false);
    try {
      navigate(gameHref(await replacePlaneGame(client)));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const action = entry?.kind === "continue" || entry?.kind === "start" ? (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="secondary" size="compact" onClick={() => setDialogOpen(true)}>
        <FormattedMessage id="grow.roleplay.card.start" defaultMessage="Play" />
      </Button>
      {error ? <span role="alert" className="smycles-field-meta text-ink-muted">
        <FormattedMessage id="grow.roleplay.card.loadError" defaultMessage="The roleplay could not be loaded." />
      </span> : null}
    </div>
  ) : (
    <span className="smycles-field-meta text-ink-muted">
      {state.status === "failed"
        ? <FormattedMessage id="grow.roleplay.card.loadError" defaultMessage="The roleplay could not be loaded." />
        : <FormattedMessage id="grow.roleplay.notYetAvailable" defaultMessage="not yet available" />}
    </span>
  );

  return <><FeaturedCard
    title={intl.formatMessage({ id: "grow.roleplay.title", defaultMessage: "Roleplay" })}
    description={intl.formatMessage({
      id: "grow.roleplay.card.description",
      defaultMessage: "Play the plane story. Choose your Powers before the story begins.",
    })}
    status={expiry ? intl.formatMessage(
      { id: "grow.roleplay.card.expires", defaultMessage: "Your open game expires on {date}" },
      { date: intl.formatDate(expiry, { day: "numeric", month: "long", year: "numeric" }) },
    ) : ""}
    eyebrow={<FormattedMessage id="grow.roleplay.card.eyebrow" defaultMessage="Play a story" />}
    actionControl={action}
    hue={262}
  />
    <Dialog open={dialogOpen} dismiss="explicit" onClose={() => setDialogOpen(false)}
      title={<FormattedMessage id="grow.roleplay.title" defaultMessage="Roleplay" />}>
      <div className="space-y-6">
        {entry?.kind === "continue" ? (
          <section className="space-y-2">
            <h3 className="text-smycles-body font-semibold text-ink">
              <FormattedMessage id="grow.roleplay.dialog.resumeTitle" defaultMessage="Your unfinished game" />
            </h3>
            <Button disabled={busy} onClick={() => navigate(gameHref(entry.gameId))}>
              <FormattedMessage id="grow.roleplay.card.continue" defaultMessage="Continue playing" />
            </Button>
          </section>
        ) : null}
        <section className="space-y-2">
          <h3 className="text-smycles-body font-semibold text-ink">
            <FormattedMessage id="grow.roleplay.dialog.scenariosTitle" defaultMessage="Scenarios" />
          </h3>
          {canStart ? (
            <div className="rounded border border-ink/20 p-3">
              <p className="mb-2 text-smycles-body text-ink">
                <FormattedMessage id="grow.roleplay.scenario.plane.title" defaultMessage="The crying baby" />
              </p>
              {entry?.kind === "continue" ? (
                <p className="mb-3 smycles-field-meta text-ink-muted">
                  <FormattedMessage id="grow.roleplay.dialog.replaceNotice" defaultMessage="Starting a new game discards your unfinished game." />
                </p>
              ) : null}
              <Button variant="secondary" disabled={busy} aria-busy={busy} onClick={() => void startNew()}>
                <FormattedMessage id="grow.roleplay.newGame" defaultMessage="Start a new game" />
              </Button>
            </div>
          ) : (
            <p className="smycles-field-meta text-ink-muted">
              <FormattedMessage id="grow.roleplay.notYetAvailable" defaultMessage="not yet available" />
            </p>
          )}
        </section>
        {error ? <p role="alert" className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="grow.roleplay.card.loadError" defaultMessage="The roleplay could not be loaded." />
        </p> : null}
        <Button variant="secondary" onClick={() => setDialogOpen(false)}>
          <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
        </Button>
      </div>
    </Dialog>
  </>;
}
